import React, { useEffect, useState, useCallback } from "react";
import { Search, ChevronDown, ChevronRight, Loader2, Package, FileText, Mail, Phone, Building2 } from "lucide-react";
import client from "@/lib/api";
import { Input } from "@/components/ui/input";

const STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  processing: "bg-blue-50 text-blue-700 border-blue-200",
  shipped: "bg-purple-50 text-purple-700 border-purple-200",
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  requested: "bg-amber-50 text-amber-700 border-amber-200",
  quoted: "bg-blue-50 text-blue-700 border-blue-200",
};

const OrdersPanel = ({ customerId }) => {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    client.get(`/admin/customers/${customerId}/orders`).then((r) => setOrders(r.data.items));
  }, [customerId]);

  if (!orders) return <div className="px-4 py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-steel-light" /></div>;
  if (orders.length === 0) return <div className="px-4 py-6 text-center text-sm text-steel-light">No orders yet.</div>;

  return (
    <div className="divide-y divide-border">
      {orders.map((o) => (
        <div key={o.id} className="px-4 py-3 flex items-start gap-3 hover:bg-surface/50">
          <div className={`mt-0.5 p-1.5 rounded-md ${o.type === "quote" ? "bg-blue-50" : "bg-orange/10"}`}>
            {o.type === "quote" ? <FileText className="w-3.5 h-3.5 text-blue-600" /> : <Package className="w-3.5 h-3.5 text-orange" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="mono text-xs font-medium text-graphite">{o.order_no}</span>
              <span className={`text-xs border rounded px-1.5 py-0.5 capitalize ${STATUS_COLORS[o.status] || "bg-surface border-border text-steel-light"}`}>{o.status}</span>
              <span className="text-xs text-steel-light mono">{o.items?.length} item{o.items?.length !== 1 ? "s" : ""}</span>
              {o.total && <span className="text-xs font-medium text-graphite mono">${Number(o.total).toFixed(2)}</span>}
            </div>
            <div className="text-xs text-steel-light mt-0.5">{new Date(o.created_at).toLocaleString()}</div>
            {o.notes && <div className="text-xs text-steel mt-1 italic">"{o.notes}"</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

const CustomerRow = ({ c }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr className="border-t border-border hover:bg-surface/50 cursor-pointer" onClick={() => setExpanded((e) => !e)}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown className="w-4 h-4 text-steel-light shrink-0" /> : <ChevronRight className="w-4 h-4 text-steel-light shrink-0" />}
            <div>
              <div className="font-medium text-graphite">{c.name || "—"}</div>
              {c.company && <div className="text-xs text-steel-light flex items-center gap-1"><Building2 className="w-3 h-3" />{c.company}</div>}
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="text-sm text-steel hover:text-orange flex items-center gap-1">
            <Mail className="w-3.5 h-3.5 shrink-0" />{c.email}
          </a>
          {c.phone && <div className="text-xs text-steel-light flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{c.phone}</div>}
        </td>
        <td className="px-4 py-3 text-center">
          {c.orders > 0 ? <span className="inline-flex items-center gap-1 mono text-sm text-graphite font-medium"><Package className="w-3.5 h-3.5 text-orange" />{c.orders}</span> : <span className="text-steel-light text-sm">—</span>}
        </td>
        <td className="px-4 py-3 text-center">
          {c.quotes > 0 ? <span className="inline-flex items-center gap-1 mono text-sm text-graphite font-medium"><FileText className="w-3.5 h-3.5 text-blue-500" />{c.quotes}</span> : <span className="text-steel-light text-sm">—</span>}
        </td>
        <td className="px-4 py-3 mono text-xs text-steel-light">
          {c.last_activity ? new Date(c.last_activity).toLocaleDateString() : (c.created_at ? new Date(c.created_at).toLocaleDateString() : "—")}
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-border bg-surface/30">
          <td colSpan={5} className="p-0">
            <OrdersPanel customerId={c.id} />
          </td>
        </tr>
      )}
    </>
  );
};

const Customers = () => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    client.get("/admin/customers", { params: search ? { search } : {} })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight text-graphite">Customers</h1>
      <p className="text-sm text-steel-light mt-1 mb-5">
        {data ? `${data.items.length} customers` : "…"} — click a row to view their orders and quotes.
      </p>

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-steel-light" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email or company…" className="pl-9 h-10" />
      </div>

      <div className="border border-border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-surface">
            <tr className="text-left mono text-xs uppercase tracking-wider text-steel-light">
              <th className="px-4 py-2.5">Customer</th>
              <th className="px-4 py-2.5">Contact</th>
              <th className="px-4 py-2.5 text-center">Orders</th>
              <th className="px-4 py-2.5 text-center">Quotes</th>
              <th className="px-4 py-2.5">Last Activity</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-steel-light" /></td></tr>}
            {!loading && data?.items?.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-steel-light text-sm">No customers yet.</td></tr>}
            {!loading && data?.items?.map((c) => <CustomerRow key={c.id} c={c} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Customers;
